import { ensureIdentity, ensureMonetization } from '@client/util/account-sdk';
import { datadogLogs } from '@datadog/browser-logs';
import {
    TRACK_PAGE_VIEW_START,
    TRACK_EVENT_START,
    TRACK_ERROR,
} from './types';
import {
    trackSuccess,
    trackError,
} from './actions';
import { selectors } from '../authentication';
import {
    formatPageViewEventParams,
    formatUIEventParams,
    formatUIImpressionEventParams,
    formatUser,
    applyExperiments,
    waitUntilTrackingReady,
} from '../../util/tracking';
import {
    formatPageViewEventParamsLegacy,
    formatUIEventParamsLegacy,
    formatUIImpressionEventParamsLegacy,
    formatActor,
} from '../../util/trackingLegacy';
import autoTracker from '../../../../tracking/autoTracker';
import {
    formatModalViewEventParams,
    formatModalCloseEventParams,
    formatModalViewEventParamsLegacy,
    formatModalCloseEventParamsLegacy,
} from '../../../../tracking/events/form';
import { HIDE_MODAL, SHOW_MODAL } from '../modals/types';
import {
    cancellationFlowModals,
    invoiceModals,
    genericModals,
} from '../../modal/types';
import { vNextModals } from '../../vNext/modal/types';
import { pulseInit } from '../../../../tracking/pulse';

const trackedModals = [
    ...cancellationFlowModals,
    ...invoiceModals,
    ...genericModals,
    ...vNextModals,
];

const buildEventPayloads = (eventType, params) => {
    switch (eventType) {
        case 'uiEvent':
            return {
                tpaas: formatUIEventParams(params),
                legacy: formatUIEventParamsLegacy(params),
            };
        case 'impression':
            return {
                tpaas: formatUIImpressionEventParams(params),
                legacy: formatUIImpressionEventParamsLegacy(params),
            };
        case 'form':
            return {
                tpaas: formatModalViewEventParams(params),
                legacy: formatModalViewEventParamsLegacy(params),
            };
        default:
            throw new Error(`Unknown tracking eventType: ${eventType}`);
    }
};

let autoTrackerCleanup = null;

export default ({ dispatch, getState }) => {
    ensureIdentity();
    ensureMonetization();

    pulseInit(getState).then(() => {
        const { Tracker: pulseTracker } = window;

        if (pulseTracker) {
            if (autoTrackerCleanup) {
                autoTrackerCleanup();
            }
            autoTrackerCleanup = autoTracker(pulseTracker, getState);
        }
    }).catch((error) => {
        console.warn('Pulse initialization error:', error); // eslint-disable-line no-console
    });

    return next => async (action) => {
        const { type } = action;
        const actionOfType = lookedFor => type === lookedFor;
        const { Tracker: pulseTracker } = window;

        if (!pulseTracker) {
            return next(action);
        }

        if (actionOfType(TRACK_PAGE_VIEW_START)) {
            await waitUntilTrackingReady(getState);
            const { pageType, customProperties = {} } = action.payload;
            const state = getState();
            const isTpaasEnabled = selectors.getIsTpaasEnabled(state);
            const legacyPayload = formatPageViewEventParamsLegacy(pageType, customProperties);
            const tpaasPayload = formatPageViewEventParams(pageType, customProperties);

            // actor/user for TPaaS events is handled automatically by the actor builder in pulse.js
            // legacy actor is still set manually here until legacy tracking is removed
            if (selectors.isLoggedInWithSPiD(state)) {
                legacyPayload.actor = formatActor(state);
            }

            applyExperiments(state);

            try {
                await pulseTracker.trackLegacyViewPage(legacyPayload);
                if (isTpaasEnabled) {
                    if (selectors.isLoggedInWithSPiD(state)) {
                        const user = formatUser(state);
                        if (user) tpaasPayload.user = user;
                    }

                    await pulseTracker.trackViewPage(tpaasPayload, { leaveTracking: false });
                }
                dispatch(trackSuccess());
            } catch (error) {
                dispatch(trackError(error));
            }
        }

        if (actionOfType(TRACK_EVENT_START)) {
            await waitUntilTrackingReady(getState);
            const state = getState();
            const isTpaasEnabled = selectors.getIsTpaasEnabled(state);
            const { eventType, params } = action.payload;
            const { tpaas, legacy } = buildEventPayloads(eventType, params);

            // actor/user for TPaaS events is handled automatically by the actor builder in pulse.js
            // legacy actor is still set manually here until legacy tracking is removed
            if (selectors.isLoggedInWithSPiD(state)) {
                legacy.actor = formatActor(state);
            }

            applyExperiments(state);

            try {
                await pulseTracker.track('trackerEvent', legacy);
                if (isTpaasEnabled) {
                    if (selectors.isLoggedInWithSPiD(state)) {
                        const user = formatUser(state);
                        if (user) tpaas.user = user;
                    }

                    if (eventType === 'impression' || eventType === 'form') {
                        await pulseTracker.trackImpressionUIElement(tpaas, { leaveTracking: false });
                    } else {
                        await pulseTracker.trackEngagementUIElement(tpaas);
                    }
                }
                dispatch(trackSuccess());
            } catch (error) {
                dispatch(trackError(error));
            }
        }

        if (actionOfType(TRACK_ERROR)) {
            const { payload } = action;
            datadogLogs.logger.warn('Tracking error', { payload });
        }

        if (actionOfType(SHOW_MODAL)) {
            const { modalType } = action;
            const result = next(action);

            if (trackedModals.includes(modalType)) {
                (async () => {
                    try {
                        await waitUntilTrackingReady(getState);
                        const state = getState();
                        const isTpaasEnabled = selectors.getIsTpaasEnabled(state);
                        const legacyPayload = formatModalViewEventParamsLegacy({ modalType });

                        if (selectors.isLoggedInWithSPiD(state)) {
                            legacyPayload.actor = formatActor(state);
                        }

                        applyExperiments(state);

                        await pulseTracker.track('trackerEvent', legacyPayload);
                        if (isTpaasEnabled) {
                            const tpaasPayload = formatModalViewEventParams({ modalType });

                            if (selectors.isLoggedInWithSPiD(state)) {
                                const user = formatUser(state);
                                if (user) tpaasPayload.user = user;
                            }

                            await pulseTracker.trackImpressionUIElement(tpaasPayload, { leaveTracking: false });
                        }
                        dispatch(trackSuccess());
                    } catch (error) {
                        dispatch(trackError(error));
                    }
                })();
            }

            return result;
        }

        if (actionOfType(HIDE_MODAL)) {
            const modalType = getState()?.modals?.current;
            const result = next(action);

            if (trackedModals.includes(modalType)) {
                (async () => {
                    try {
                        await waitUntilTrackingReady(getState);
                        const state = getState();
                        const isTpaasEnabled = selectors.getIsTpaasEnabled(state);
                        const legacyPayload = formatModalCloseEventParamsLegacy({ modalType });

                        if (selectors.isLoggedInWithSPiD(state)) {
                            legacyPayload.actor = formatActor(state);
                        }

                        applyExperiments(state);

                        await pulseTracker.track('trackerEvent', legacyPayload);
                        if (isTpaasEnabled) {
                            const tpaasPayload = formatModalCloseEventParams({ modalType });

                            if (selectors.isLoggedInWithSPiD(state)) {
                                const user = formatUser(state);
                                if (user) tpaasPayload.user = user;
                            }

                            await pulseTracker.trackImpressionUIElement(tpaasPayload, { leaveTracking: false });
                        }
                        dispatch(trackSuccess());
                    } catch (error) {
                        dispatch(trackError(error));
                    }
                })();
            }

            return result;
        }

        return next(action);
    };
};
